import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { PopperStorybookFriendly } from "utils/storyUtils";
import type { TimeRangeFinite, Process } from "types/types";
import {
  useFormatZoned,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { useBreakpoints, usePrevious, Z_INDEX } from "constants/constants";
import {
  useIsRetrotagPopperOpen,
  useRetrotagSegmentRange,
} from "utils/customHooks/retrotagSliceSelectors";
import {
  clearRetrotagState,
  updateRetrotagSegmentRange,
} from "store/retrotagSlice";
import { useDispatch } from "react-redux";
import {
  RetrotagPopperFormPage as FormPage,
  RetrotagPopperSubmittedPage as SubmittedPage,
} from "./RetrotagPopperPages";
import {
  PopperContentStyles,
  RETROTAG_POPPER_HEIGHT,
} from "./RetrotagPopperStyles";
import { useLocation } from "react-router";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useApplyRetrotagMutation } from "./retrotagMutationHooks";
import {
  FORMAT_DAY_TIME_WITH_SECONDS,
  getFormattedDurationTimes,
  ONE_SECOND_OF_MS,
} from "utils/timeUtils";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsDarkMode } from "store/atoms";
import { FormatZonedFn } from "utils/customHooks/genericHooksTypes";
import { Check, Close, Edit } from "@mui/icons-material";
import { Button, IconButton, PopperPlacementType } from "@mui/material";
import { format } from "date-fns";
import { clamp, isEqual } from "lodash";
import LogRocket from "logrocket";
import { useIsTimelineExpanded } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";

// Use React.memo to prevent unnecessary re-renders
const RetrotagPopper = memo(function RetrotagPopper({
  process,
}: {
  process?: Process;
}) {
  const isStorybook = useIsStorybook();
  const open = useIsRetrotagPopperOpen(process?.id || "");

  // Skip remaining expensive calculations if popper isn't visible
  // This prevents unnecessary work during zoom operations
  if (!open && !isStorybook) {
    return null;
  }

  return <RetrotagPopperContent process={process} />;
});

RetrotagPopper.displayName = "RetrotagPopper";
function RetrotagPopperContent({ process }: { process?: Process }) {
  const isStorybook = useIsStorybook();
  const dispatch = useDispatch();
  const { isDesktopXlOrLarger } = useBreakpoints();
  const formatZoned: FormatZonedFn = useFormatZoned();
  const [hasSubmittedForm, setHasSubmittedForm] = useState(false);
  const [mutationFailedErrorMsg, setMutationFailedErrorMsg] = useState<
    string | null
  >(null);
  const retrotagSegmentRange = useRetrotagSegmentRange();
  const { translate } = useFlightTranslation();
  const [defaultProcess] = useVisibleProcesses({
    getSingleProcessOnly: true,
  });

  // Calculate duration here only when needed
  const duration = useRetrotagSegmentDuration();

  const location = useLocation();
  const prevLocation = usePrevious(location);

  const handleClose = useCallback(() => {
    dispatch(clearRetrotagState());
    setHasSubmittedForm(false);
    setMutationFailedErrorMsg(null);
    setEditRangeOpen(false);
    setEditedRetrotagSegmentRange(null);
    setSelectedProdSegmentRange(null);
  }, [dispatch]);

  useEffect(() => {
    if (prevLocation && prevLocation !== location && !isStorybook) {
      handleClose();
    }
  }, [prevLocation, location, isStorybook, handleClose]);
  useEffect(() => {
    LogRocket.track("Retrotag Popper Opened");
  }, []);
  const anchorEl = document.getElementById(`timelineStyles`);

  const machemetryId = process?.id ?? defaultProcess?.id;

  const [mutateSegment] = useApplyRetrotagMutation({
    machemetryId,
    skip: !open,
  });

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoadingResponse(true);
    try {
      await mutateSegment();
    } catch (error) {
      setMutationFailedErrorMsg(String(error));
    }
    setHasSubmittedForm(true);
    setLoadingResponse(false);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (hasSubmittedForm) {
      timer = setTimeout(handleClose, mutationFailedErrorMsg ? 5000 : 300);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [hasSubmittedForm, handleClose, mutationFailedErrorMsg]);

  useEffect(() => {
    return function cleanup() {
      handleClose();
    };
  }, [handleClose]);
  const [isDarkMode] = useIsDarkMode();
  const [editRangeOpen, setEditRangeOpen] = useState(false);
  const [editedRetrotagSegmentRange, setEditedRetrotagSegmentRange] =
    useState<TimeRangeFinite | null>(null);
  // we want to access selectedProdSegment.range but it's not available in this context; equivalent should be retrotag initial range when popper is mounted, so use that as proxy
  const [selectedProdSegmentRange, setSelectedProdSegmentRange] =
    useState<TimeRangeFinite | null>(null);
  // when retrotag range is first set (from null to non-null), record it
  useEffect(() => {
    if (
      retrotagSegmentRange &&
      !isEqual(retrotagSegmentRange, selectedProdSegmentRange)
    ) {
      setSelectedProdSegmentRange(retrotagSegmentRange);
      setEditedRetrotagSegmentRange(retrotagSegmentRange);
    }
  }, [retrotagSegmentRange, selectedProdSegmentRange]);

  const error =
    editedRetrotagSegmentRange &&
    editedRetrotagSegmentRange.ge >= editedRetrotagSegmentRange.lt
      ? "Invalid range - start cannot be <= end"
      : null;
  const disabled = Boolean(error);
  // Calculate the appropriate placement based on anchor element position
  const [placement, setPlacement] = useState<PopperPlacementType>("top");

  // Function to calculate the appropriate placement
  const calculatePlacement = useCallback(() => {
    if (!anchorEl) {
      return "top";
    }

    // Get the position of the anchor element
    const anchorRect = anchorEl.getBoundingClientRect();

    // Get the height of the popper (including the duration display and edit controls)
    const popperTotalHeight = RETROTAG_POPPER_HEIGHT + 100; // Add extra space for duration display and buttons

    // Check if there's enough space above the anchor
    const hasEnoughSpaceAbove = anchorRect.top > popperTotalHeight;

    // Check if popper would overflow top of viewport
    const wouldOverflowTop = anchorRect.top - popperTotalHeight < 0;

    // Always use bottom placement if it would overflow the top,
    // otherwise use top if there's enough space above
    return wouldOverflowTop ? "bottom" : hasEnoughSpaceAbove ? "top" : "bottom";
  }, [anchorEl]);

  // Set initial placement
  useEffect(() => {
    const newPlacement = calculatePlacement();
    setPlacement(newPlacement);
  }, [calculatePlacement]);

  // Update placement on scroll
  useEffect(() => {
    if (!open) {
      return;
    }

    // Create a more aggressive scroll handler that prioritizes preventing timeline overlap
    const handleScroll = () => {
      if (!anchorEl) {
        return;
      }

      // Get more accurate sizing with extra safety margin
      const popperTotalHeight = RETROTAG_POPPER_HEIGHT + 120; // Add extra safety margin

      // grab the element data-component-name="Timeline" > foreignObject > canvas and detect its offset top,
      // then determine if the popper would overlap the timeline from the popper's bottom
      const timelineOffsetTop = document
        .querySelector('[data-component-name="Timeline"] canvas')
        ?.getBoundingClientRect().top;

      const remainingRoom =
        timelineOffsetTop && timelineOffsetTop - popperTotalHeight;

      // Calculate the point at which the popper would start to overlap the timeline
      // This ensures we switch to bottom placement well before any overlap could occur
      const wouldOverlapTimelineFromTop = remainingRoom && remainingRoom < 100;

      // ALWAYS use bottom placement if there's any risk of timeline overlap
      // This is a more aggressive approach to prevent any possible overlap
      const newPlacement = wouldOverlapTimelineFromTop ? "bottom" : "top";

      if (newPlacement !== placement) {
        setPlacement(newPlacement);
      }
    };

    // Run once immediately to ensure correct initial position
    handleScroll();

    // Find all potentially scrollable elements that could affect the popper position
    const scrollableElements: Element[] = [];

    // Always add window
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Find the datepickerAndCharts element for hublet page scrolling
    const datepickerAndChartsElement = document.querySelector(
      ".datepickerAndCharts"
    );
    if (datepickerAndChartsElement) {
      datepickerAndChartsElement.addEventListener("scroll", handleScroll, {
        passive: true,
      });
      scrollableElements.push(datepickerAndChartsElement);
    }

    // Find any other scrollable parent containers that might affect positioning
    const findScrollableParents = (element: Element | null): void => {
      if (!element || element === document.documentElement) {
        return;
      }

      // Check if this element can scroll
      const style = window.getComputedStyle(element);
      const overflow =
        style.getPropertyValue("overflow") ||
        style.getPropertyValue("overflow-y") ||
        style.getPropertyValue("overflow-x");

      if (overflow.includes("auto") || overflow.includes("scroll")) {
        element.addEventListener("scroll", handleScroll, { passive: true });
        scrollableElements.push(element);
      }

      // Check its parent
      findScrollableParents(element.parentElement);
    };

    // Start from the anchorEl and find all scrollable parents
    if (anchorEl) {
      findScrollableParents(anchorEl);
    }

    return () => {
      // Clean up all event listeners
      window.removeEventListener("scroll", handleScroll);

      scrollableElements.forEach(element => {
        element.removeEventListener("scroll", handleScroll);
      });
    };
  }, [placement, calculatePlacement, anchorEl]);

  // Define a consistent position for the popper to appear when it's in bottom placement
  // We want it to always sit just below the main timeline regardless of expanded timeline state
  const modifiers = useMemo(() => {
    // Common modifiers for both placements
    const commonModifiers = [
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
          altAxis: true,
          padding: 5, // Add padding to ensure popper stays within viewport
        },
      },
    ];

    if (placement === "bottom") {
      return [
        ...commonModifiers,
        // Set a specific offset to position just below the main timeline
        {
          name: "offset",
          options: {
            // A small positive offset positions it directly below the timeline
            // regardless of expanded timeline state
            offset: [0, 2],
          },
        },
        // This modifier ensures the popper is positioned at the exact location
        // we specify and doesn't get pushed down by expanded timelines
        {
          name: "computeStyles",
          options: {
            gpuAcceleration: false, // Better positioning control
          },
        },
        // Force the popper to be positioned relative to the main timeline
        // and not pushed down by expanded timelines
        {
          name: "flip",
          options: {
            fallbackPlacements: [], // Disable automatic flipping
            rootBoundary: "document",
          },
        },
        // This ensures the popper follows its reference (the timeline)
        // precisely as intended, without being affected by other elements
        {
          name: "popperOffsets",
          enabled: true,
          options: {},
        },
      ];
    } else {
      return [
        ...commonModifiers,
        {
          name: "offset",
          options: {
            offset: [0, 5], // Small offset for top placement
          },
        },
      ];
    }
  }, [placement]);
  const [loadingResponse, setLoadingResponse] = React.useState(false);
  const [isTimelineExpanded] = useIsTimelineExpanded();

  return (
    <PopperStorybookFriendly
      open={true}
      anchorEl={anchorEl}
      modifiers={modifiers}
      aria-labelledby="popper-title"
      aria-describedby="popper-description"
      style={{ zIndex: Z_INDEX.L12 }}
    >
      <div className="space space-30" style={{ height: "30px" }} />
      <PopperContentStyles
        $isDarkMode={isDarkMode}
        $isTimelineExpanded={isTimelineExpanded}
      >
        <div className="paper">
          {hasSubmittedForm ? (
            <SubmittedPage errorMsg={mutationFailedErrorMsg} />
          ) : (
            <FormPage
              handleClose={handleClose}
              handleFormSubmit={handleFormSubmit}
              processId={machemetryId}
              loadingResponse={loadingResponse}
            />
          )}
        </div>
        <div className="durationDisplay">
          {translate("Duration")}: {duration}
        </div>
        {retrotagSegmentRange ? (
          <div>
            {editRangeOpen && editedRetrotagSegmentRange ? (
              <>
                <div className="startEndTimesDisplay editStartEndTimes">
                  <input
                    style={isDesktopXlOrLarger ? {} : { width: 196 }}
                    step="1"
                    type="datetime-local"
                    value={format(
                      Number(editedRetrotagSegmentRange.ge),
                      "yyyy-MM-dd'T'HH:mm:ss"
                    )}
                    min={format(
                      Number(selectedProdSegmentRange?.ge ?? Date.now()),
                      "yyyy-MM-dd'T'HH:mm"
                    )}
                    onChange={e => {
                      const newGe = new Date(e.target.value).getTime();
                      const ge = selectedProdSegmentRange
                        ? clamp(
                            newGe,
                            selectedProdSegmentRange.ge,
                            selectedProdSegmentRange.lt
                          )
                        : newGe;
                      if (isValidTimestamp(ge)) {
                        setEditedRetrotagSegmentRange({
                          ...editedRetrotagSegmentRange,
                          ge,
                        });
                      }
                    }}
                  />
                  {" — "}
                  <input
                    style={isDesktopXlOrLarger ? {} : { width: 196 }}
                    step="1"
                    type="datetime-local"
                    value={format(
                      Number(editedRetrotagSegmentRange.lt),
                      "yyyy-MM-dd'T'HH:mm:ss"
                    )}
                    max={format(
                      Number(selectedProdSegmentRange?.lt ?? Date.now()),
                      "yyyy-MM-dd'T'HH:mm"
                    )}
                    onChange={e => {
                      const newLt = new Date(e.target.value).getTime();
                      const lt = selectedProdSegmentRange
                        ? clamp(
                            newLt,
                            selectedProdSegmentRange.ge,
                            selectedProdSegmentRange.lt
                          )
                        : newLt;
                      if (isValidTimestamp(lt)) {
                        setEditedRetrotagSegmentRange({
                          ...editedRetrotagSegmentRange,
                          lt,
                        });
                      }
                    }}
                  />
                </div>
                <div className="error">{error}</div>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditedRetrotagSegmentRange(retrotagSegmentRange);
                    setEditRangeOpen(false);
                  }}
                >
                  <Close />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={disabled}
                  style={disabled ? { opacity: 0.5 } : {}}
                  onClick={() => {
                    if (
                      editedRetrotagSegmentRange.ge <
                      editedRetrotagSegmentRange.lt
                    ) {
                      setEditRangeOpen(false);
                      setEditedRetrotagSegmentRange(retrotagSegmentRange);
                      dispatch(
                        updateRetrotagSegmentRange(editedRetrotagSegmentRange)
                      );
                    }
                  }}
                >
                  <Check />
                </IconButton>
              </>
            ) : (
              <>
                <div className="startEndTimesDisplay">
                  {"("}
                  {formatZoned(
                    Number(retrotagSegmentRange.ge),
                    FORMAT_DAY_TIME_WITH_SECONDS
                  )}{" "}
                  —{" "}
                  {formatZoned(
                    Number(retrotagSegmentRange.lt),
                    FORMAT_DAY_TIME_WITH_SECONDS
                  )}
                  {")"}
                </div>
                <Button
                  className="editRangeButton"
                  size="small"
                  style={{ color: "white" }}
                  onClick={() => {
                    setEditedRetrotagSegmentRange(retrotagSegmentRange);
                    setEditRangeOpen(true);
                  }}
                  endIcon={<Edit />}
                  variant="contained"
                >
                  {translate("Edit Range")}
                </Button>
              </>
            )}
          </div>
        ) : null}
      </PopperContentStyles>
    </PopperStorybookFriendly>
  );
}

// Export the memoized component
export default RetrotagPopper;

// Helper function to validate timestamps within the component
function isValidTimestamp(timestamp: number): boolean {
  const date = new Date(timestamp);
  return (
    String(timestamp).length === 13 /* String(Date.now()).length */ &&
    !isNaN(date.getTime())
  );
}

// Function to calculate the duration display string
function useRetrotagSegmentDuration(): string {
  const range = useRetrotagSegmentRange();
  const durationMs = range ? range.lt - range.ge : 0;
  const formatZoned = useFormatZoned();
  const { translate } = useFlightTranslation();

  const {
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    isLessThanOneMinute,
  } = getFormattedDurationTimes(durationMs, formatZoned, translate);
  return isLessThanOneMinute
    ? `${(durationMs / ONE_SECOND_OF_MS).toFixed(0)}${translate(
        "seconds_short_hand"
      )}`
    : `${displayedTime1}${displayedUnit1} ${displayedTime2 ?? ""}${
        displayedUnit2 ?? ""
      }`;
}
